/**
 * Running the analyses, one step at a time.
 *
 * Two things this screen was corrected on:
 *
 * **It does not measure quality itself.** The verdicts it reports are the ones
 * the session screen already read, handed down as `quality`. Fetching them
 * again here meant every session screen asked the server for the same
 * assessments twice.
 *
 * **An empty session has completed nothing.** With no files, the quality step
 * used to summarise an empty list - "0 dosya ölçüldü" - and badge itself
 * "Tamamlandı", which told a user their analysis was done before they had
 * uploaded anything.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import {
  getFaceAnalysis, getHeadCoverage, getHumanAnalysis, getKeyframeSelection,
  listAllSessionMedia, runFaceAnalysis, runHeadCoverage, runHumanAnalysis,
  runKeyframeSelection, runQualityAssessment,
} from "@/api/client";
import { ApiError } from "@/api/errors";
import type { MediaAsset, MediaCountSummary, SessionQualitySummary } from "@/api/types";
import { errorMessage } from "@/components/ErrorState";
import { Button, Callout, Spinner, StatusBadge } from "@/components/ui";
import { NOT_YET } from "@/i18n/terms";
import { PROBLEM_STATUSES } from "./mediaAnalysis";
import { type SessionQuality } from "./useMediaAnalyses";
import "./sessions.css";

type StepKey = "selection" | "frame" | "session" | "coverage";
type StepState =
  | { phase: "checking" }
  | { phase: "absent" }
  | { phase: "running" }
  | { phase: "ready"; detail: string }
  | { phase: "failed"; reason: string }
  /*
   * A closed badge vocabulary, kept small on purpose: a neutral badge is read
   * at a glance and three or four phrases is what somebody can tell apart.
   * "Başka ekranda" is the fourth and the only one that means *the answer
   * exists, elsewhere* - the other three all mean there is no answer.
   */
  | { phase: "neutral"; label: "Uygulanamaz" | "Desteklenmiyor" | "Henüz çalıştırılmadı" | "Başka ekranda"; detail: string };
interface Step {
  key: StepKey; title: string; description: string; prerequisite?: StepKey;
  load: (signal: AbortSignal) => Promise<StepState>; run?: () => Promise<string>;
}
const ready = (detail: string): StepState => ({ phase: "ready", detail });
const absent = (): StepState => ({ phase: "absent" });

export function AnalysisSteps({ sessionId, media, mediaTotals, qualitySummary, quality, onCompleted, headAnalysis = true, analysisKind = "human_head" }: {
  sessionId: string;
  /** The page the grid is holding. Used for *nothing* that is a count. */
  media: MediaAsset[];
  /** What the session actually holds, counted by the server. */
  mediaTotals: MediaCountSummary | null;
  /** The session's verdicts, counted by the server. */
  qualitySummary: SessionQualitySummary | null;
  /** The session's quality assessments, read once by the screen above. */
  quality: SessionQuality;
  onCompleted: () => void; headAnalysis?: boolean;
  analysisKind?: "human_head" | "human_body" | "object" | "spatial" | "human_in_environment";
}) {
  const mediaKey = media.map((asset) => `${asset.id}:${asset.media_type}`).join(",");
  /*
   * Every count below comes from the server's own totals, never from `media`.
   *
   * `media` is one page of a grid. Deriving "how many files are there" from it
   * measured a 150-file session 100 files deep, reported "100 dosya ölçüldü",
   * and badged the step Tamamlandı while fifty files had never been looked at.
   * The fallback to the page exists only for the moment before the summaries
   * arrive, and it is a floor, never a total.
   */
  const totalMedia = mediaTotals?.total ?? media.length;
  const hasMedia = totalMedia > 0;
  const inFlight = useRef(new Set<StepKey>());
  /*
   * `quality` is a dependency, and may be one: `useMediaAnalyses` returns the
   * same object while the answers are the same. The chain is rebuilt when a
   * verdict changes and not once per render.
   */
  const chain = useMemo((): Step[] => {
    const entries = mediaKey ? mediaKey.split(",") : [];
    const assetId = (entry: string) => entry.replace(/:(photo|video)$/, "");
    const videos = entries.filter((entry) => entry.endsWith(":video")).map(assetId);
    const photoCount = mediaTotals?.photos ?? entries.length - videos.length;
    /**
     * Every file in the session, read when a step actually *runs*.
     *
     * Not at render: this walks every page, and a screen that paged the whole
     * session on mount would undo the reason the grid is paged at all. A run
     * is a deliberate click, and it has to act on the capture rather than on
     * the scroll position.
     */
    const allMedia = () => listAllSessionMedia(sessionId);
    const selection: Step = videos.length ? {
      key: "selection", title: "Kare Seçimi",
      description: "Videolardan analiz edilecek gerçek kareler seçilir; fotoğraflar doğrudan hazırlanır.",
      load: async (signal) => {
        try {
          const rows = await Promise.all(videos.map((id) => getKeyframeSelection(id, { signal })));
          return ready(selectionSummary(rows, photoCount));
        } catch (error) { if (isMissing(error)) return absent(); throw error; }
      },
      run: async () => {
        const rows = [];
        for (const item of (await allMedia()).filter((a) => a.media_type === "video"))
          rows.push(await runKeyframeSelection(item.id));
        return selectionSummary(rows, photoCount);
      },
    } : {
      key: "selection", title: "Kare Seçimi",
      description: "Yüklenen fotoğraflar kare seçimi gerektirmeden analiz girdisi olarak hazırlanır.",
      load: async () => hasMedia ? ready(`${photoCount} fotoğraf analiz için hazır`) : absent(),
    };
    /*
     * A place with people in it runs the face model, and stops there.
     *
     * It reached production running the *head-scan* chain, because it has
     * `head_analysis` and that flag was the only thing this screen asked. So a
     * room capture was told its photographs would be judged as "beş bölgeli
     * baş kapsaması" and step 04 measured five-region head coverage - a truth
     * about a portrait session, offered for a room.
     *
     * The frame step is real and is the input everything else needs: it is how
     * people are located at all. What follows it is not a head merge. The
     * per-person tracks and the place's own coverage are derived on read by
     * `GET /projects/{id}/scene-coverage`, reported separately and never as
     * one percentage, so those two steps say where the answer is rather than
     * offering a button that would run the wrong measurement.
     */
    if (analysisKind === "human_in_environment") return [selection,
      operation("frame", "Kare Analizi", "Her karede kişilerin bulunduğu yerler ölçülür. Kimlik tanıma yapılmaz.", "selection", (s) => getFaceAnalysis(sessionId, { signal: s }), () => runFaceAnalysis(sessionId)),
      {
        key: "session", title: "Oturum Birleştirme",
        description: "Kareler arası kişi izleri oturum içinde tutulur; kalıcı bir kimlik kaydı oluşturulmaz.",
        load: async () => ({ phase: "neutral", label: "Başka ekranda", detail: "Kişi izleri Kapsama okunurken çıkarılır; ayrı bir aşama değildir." }),
      },
      {
        key: "coverage", title: "Oturum Kapsaması",
        description: "Mekân kapsaması ile her kişinin kapsaması ayrı ayrı raporlanır; tek bir birleşik yüzde üretilmez.",
        load: async () => ({ phase: "neutral", label: "Başka ekranda", detail: "Mekân, kişiler ve sonraki çekim Kapsama sekmesinde ayrı ayrı gösterilir." }),
      },
    ];
    if (headAnalysis) return [selection,
      operation("frame", "Kare Analizi", "Her karede yüz varlığı ve desteklenen baş açısı ölçülür. Kimlik tanıma yapılmaz.", "selection", (s) => getFaceAnalysis(sessionId, { signal: s }), () => runFaceAnalysis(sessionId)),
      operation("session", "Oturum Birleştirme", "Geçerli kare kanıtları birleştirilerek ölçülen baş görünümleri çıkarılır.", "frame", (s) => getHumanAnalysis(sessionId, { signal: s }), () => runHumanAnalysis(sessionId)),
      operation("coverage", "Oturum Kapsaması", "Birleştirilmiş kanıt beş bölgeli baş kapsamasıyla değerlendirilir; final model iddiası değildir.", "session", (s) => getHeadCoverage(sessionId, { signal: s }), () => runHeadCoverage(sessionId)),
    ];
    const frameDescription = analysisKind === "human_body"
      ? "Vücut bölgelerini ölçen bir model yoktur; yalnız gerçek medya kalitesi ölçülür."
      : analysisKind === "object" ? "Nesne yönleri atanmaz; yalnız gerçek medya kalitesi ölçülür."
      : "Duvar, cephe veya sahne nesnesi sınıflandırılmaz; yalnız gerçek medya kalitesi ölçülür.";
    return [selection, {
      key: "frame", title: "Kare Analizi", prerequisite: "selection", description: frameDescription,
      load: async () => qualityStepState(qualitySummary, quality.loading),
      run: async () => {
        let measured = 0;
        for (const item of await allMedia()) { await runQualityAssessment(item.id); measured += 1; }
        return `${measured} dosya ölçüldü`;
      },
    }, {
      key: "session", title: "Oturum Birleştirme",
      description: "Bu konu türünde semantik bölge sonucu üreten bir oturum birleştirme modeli yoktur.",
      load: async () => ({ phase: "neutral", label: "Uygulanamaz", detail: "Kalite kanıtı dosya düzeyinde korunur; sahte semantik kanıt üretilmez." }),
    }, {
      key: "coverage", title: "Oturum Kapsaması",
      description: "Yetkili kapsama, uyumlu seyrek rekonstrüksiyondan gelen göreli kamera pozu kanıtına dayanır.",
      load: async () => ({ phase: "neutral", label: "Henüz çalıştırılmadı", detail: "Kapsama sekmesinde doğrulanmış seyrek rekonstrüksiyon kaynağı okunur." }),
    }];
  }, [sessionId, mediaKey, headAnalysis, analysisKind, hasMedia, quality, mediaTotals, qualitySummary]);
  const [states, setStates] = useState<Record<string, StepState>>({});
  useEffect(() => {
    const controller = new AbortController();
    setStates(Object.fromEntries(chain.map((step) => [step.key, { phase: "checking" as const }])));
    for (const step of chain) step.load(controller.signal).then((state) => {
      if (!controller.signal.aborted) setStates((current) => ({ ...current, [step.key]: state }));
    }).catch((error: unknown) => {
      if (!controller.signal.aborted) setStates((current) => ({ ...current, [step.key]: { phase: "failed", reason: error instanceof ApiError ? errorMessage(error) : "Durum okunamadı." } }));
    });
    return () => controller.abort();
  }, [chain]);
  const execute = async (step: Step) => {
    if (!step.run || inFlight.current.has(step.key)) return;
    inFlight.current.add(step.key); setStates((s) => ({ ...s, [step.key]: { phase: "running" } }));
    try { const detail = await step.run(); setStates((s) => ({ ...s, [step.key]: ready(detail) })); onCompleted(); }
    catch (caught) { const error = caught instanceof ApiError ? caught : new ApiError({ kind: "unknown", url: "" }); setStates((s) => ({ ...s, [step.key]: { phase: "failed", reason: explain(step.key, error) } })); }
    finally { inFlight.current.delete(step.key); }
  };
  return <section className="stack analysis-pipeline" aria-labelledby="analysis-pipeline-title">
    <div><h2 id="analysis-pipeline-title">Analizi çalıştır</h2><p className="muted">Yüklediğiniz dosyaların ölçülmesi için aşağıdaki adımları sırayla çalıştırın.</p></div>
    {/*
      The empty session says what to do, rather than offering four disabled
      buttons and a set of statuses about nothing.
    */}
    {!hasMedia && <Callout title="Analiz için önce fotoğraf yükleyin">Bu oturumda henüz dosya yok. Yukarıdaki "Dosya yükle" bölümünden fotoğraf ekledikten sonra analiz adımları çalıştırılabilir.</Callout>}
    {/*
      The vocabulary a reader needs only if they ask. "Analiz Pipeline V3",
      "GERÇEK ANALİZ ZİNCİRİ" and the per-subject evidence limit were the
      first three things on this screen and none of them answered a question a
      customer had.
    */}
    <details className="analysis-technical">
      <summary>Teknik ayrıntılar</summary>
      <div className="stack-tight">
        <p className="muted">{NOT_YET.orchestration}</p>
        {!headAnalysis && <p className="muted">{subjectLimit(analysisKind)}</p>}
      </div>
    </details>
    <ol className="steps" aria-label="Dört aşamalı analiz zinciri">{chain.map((step, index) => {
      const state = states[step.key] ?? { phase: "checking" as const };
      const predecessor = step.prerequisite ? states[step.prerequisite] : null;
      const blocked = !hasMedia ? "Önce bu oturuma en az bir dosya yükleyin." : step.prerequisite && predecessor?.phase !== "ready" ? "Önceki adımı tamamlayın." : undefined;
      return <li className="step" key={step.key}>
        <span className={state.phase === "ready" ? "step__index step__index--done" : "step__index"} aria-label={`${index + 1}. aşama`}>{String(index + 1).padStart(2, "0")}</span>
        <div className="stack-tight"><span className="step__title">{step.title}</span><span className="muted">{step.description}</span><StepStatus state={state} /></div>
        <div className="step__action">{step.run && <Button variant={state.phase === "ready" ? "secondary" : "primary"} busy={state.phase === "running"} disabled={state.phase === "checking" || state.phase === "running" || Boolean(blocked)} title={blocked} onClick={() => void execute(step)}>{state.phase === "ready" ? "Yeniden Çalıştır" : "Çalıştır"}</Button>}</div>
      </li>;
    })}</ol>
  </section>;
}

function operation(key: StepKey, title: string, description: string, prerequisite: StepKey, load: (signal: AbortSignal) => Promise<{version:number}>, run: () => Promise<{version:number}>): Step {
  return { key, title, description, prerequisite, load: async (signal) => { try { return ready(versionOf(await load(signal))); } catch (error) { if (isMissing(error)) return absent(); throw error; } }, run: async () => versionOf(await run()) };
}
const isMissing = (error: unknown) => error instanceof ApiError && error.kind === "not_found";
const versionOf = (result: {version:number}) => `sürüm ${result.version}`;
const selectionSummary = (rows: {selected_count:number;candidate_count:number}[], photos: number) => `${rows.reduce((n,r)=>n+r.candidate_count,0)} adaydan ${rows.reduce((n,r)=>n+r.selected_count,0)} kare seçildi${photos ? `; ${photos} fotoğraf doğrudan hazır` : ""}`;
/**
 * What the quality step says, computed from the *server's* counts.
 *
 * It used to be computed from the assessments the grid had loaded, which made
 * the answer a function of the scroll position: a 150-file session whose first
 * hundred were all measured reported "100 dosya ölçüldü" and badged itself
 * Tamamlandı, with fifty files never looked at and nothing on screen saying so.
 * The server already counts this - `/capture-sessions/{id}/quality-summary`
 * joins the newest verdict per file - so the only correct source is that.
 *
 * The two things that have not changed: `absent` for a session with no files,
 * because nothing has been measured rather than everything; and a partly
 * measured session is *not* complete - it reports the fraction and keeps the
 * run button primary, because "Tamamlandı" over a partial measurement is the
 * same lie in a smaller size. `loading` is still the page's, because it is the
 * one thing the summary cannot say: whether this screen has finished asking.
 */
export function qualityStepState(
  summary: SessionQualitySummary | null,
  loading: boolean,
): StepState {
  if (summary === null) return loading ? { phase: "checking" } : absent();
  if (summary.total === 0) return absent();
  if (summary.assessed === 0) return absent();
  const problems = PROBLEM_STATUSES.reduce(
    (count, status) => count + (summary.statuses[status] ?? 0),
    0,
  );
  const pending = summary.unassessed;
  const measured = `${summary.assessed} dosya ölçüldü; ${problems} dosyada sorun bulundu`;
  return pending > 0
    ? {
        phase: "neutral",
        label: "Henüz çalıştırılmadı",
        detail: `${measured}; ${pending} dosya henüz ölçülmedi`,
      }
    : ready(measured);
}
const subjectLimit = (kind: string) => kind === "human_body" ? "Semantik vücut bölgeleri ölçülmez. Genel kalite ve rekonstrüksiyon kökenli kamera kanıtı ayrı tutulur." : kind === "object" ? "Nesnenin önü veya arkası atanmaz. Genel kalite ve göreli görünüm kanıtı ayrı tutulur." : "Duvar, zemin, tavan, cephe veya sahne tamamlanması üretilmez. Mekânsal kanıt seyrek rekonstrüksiyondan gelir.";
function explain(step: StepKey, error: ApiError) { if (step === "frame" && ["validation","conflict"].includes(error.kind)) return "Analiz edilecek kare yok. Önce Kare Seçimi aşamasını tamamlayın."; if (error.kind === "conflict") return "Bu aşama için gereken önceki sonuç eksik."; return errorMessage(error); }
function StepStatus({state}:{state:StepState}) {
  if (state.phase === "checking") return <span className="faint"><Spinner inline /> Durum okunuyor</span>;
  if (state.phase === "absent") return <StatusBadge tone="neutral">Henüz çalıştırılmadı</StatusBadge>;
  if (state.phase === "ready") return <span className="row"><StatusBadge tone="success">Tamamlandı</StatusBadge><span className="faint">{state.detail}</span></span>;
  if (state.phase === "running") return <span className="faint"><Spinner inline /> Çalışıyor</span>;
  if (state.phase === "neutral") return <span className="stack-tight"><StatusBadge tone="neutral">{state.label}</StatusBadge><span className="faint">{state.detail}</span></span>;
  return <span><StatusBadge tone="caution">Sorun bulundu</StatusBadge> {state.reason}</span>;
}
